import { bucketLabel, relabelBucket } from "@/lib/bucketLabels";
import apiClient from "./client";
import type { AllocationCard, PlanEntry, QuickTip } from "@/data/mockDashboard";

/* ─────────────────────────── API response types ───────────────────────────
 * Mirrors backend/apps/insights/serializers.py. Unlike the dashboard endpoint,
 * monetary/percentage values are JSON numbers (DRF FloatField/IntegerField),
 * so no string→number parsing is needed. */

export interface InsightSnapshotSegment {
  /** `debt_payments` is FA's key for ACTUAL debt payments. It was absent from this
   *  union (and from the backend loop) which is why $716/mo never reached the
   *  dashboard and the /insights tiles summed to 86.9% (BEN-141 4a FIX 2). */
  /** FP-176 extension: `uncategorized`, sent only when some spending needs a category. */
  key: "needs" | "wants" | "savings" | "debt_payments" | "uncategorized";
  monthly: number;
  total: number;
  pct: number;
}

/**
 * The five terms of the top-line equation, computed ONCE on the server.
 *
 * BEN-141 4a D6 / 4b 1c. This used to be derived in `AllocationCardsRow` from cards
 * that this file assembled out of two unrelated sources — the savings segment plus
 * `found.suggested_allocation.debt_payoff`, a slice of SURPLUS worth ~$6.15/mo on uid
 * 270. The tile therefore read "Savings/Debt $1,147" while claiming "Savings & Goals +
 * Debt Payoff" and carrying neither, and Safe to Spend was overstated by the whole
 * $716 of real debt payments. Read these; derive nothing.
 */
export interface InsightEquation {
  income: number;
  needs: number;
  wants: number;
  savings: number;
  debt_payments: number;
  /** FP-176 extension: spending that still needs a category (0 when none). Optional
   *  only so an older cached payload still reads; the server always sends it. */
  uncategorized?: number;
  /** SIGNED. Negative means the user is over budget for a typical month. */
  safe_to_spend: number;
  /** Explicit, so a client renders over-budget framing from a flag rather than
   *  inferring intent from a minus sign. */
  over_budget: boolean;
  /** Magnitude of the overspend, 0 when solvent. */
  shortfall: number;
  /** Five terms; sums to exactly 100.0 WITH debt included. */
  percentages: Record<string, number>;
  /** MONTHLY goal-ledger savings with no backing transaction — the amount by which
   *  FA §4 legitimately exceeds what §6 can see, since §6 walks transactions only.
   *  Carried so the §4/§6 difference is explained from DATA, not hardcoded copy. */
  untwinned_contributions: number;
}

/**
 * RD-11 (report 131): the typical month, defined once on the server
 * (`forecasting/services/typical_month.py`). Every page that shows monthly income,
 * bills or money left over reads these; nothing here recomputes them.
 *
 * `income` and `left_over` are null when no paycheck has been found: the app never
 * invents an income. The five figures sum to `income`.
 */
export interface TypicalMonth {
  has_paycheck: boolean;
  income: number | null;
  bills: number;
  everyday: number;
  everyday_floored: boolean;
  saving: number;
  debt: number;
  left_over: number | null;
  spending_total: number;
  /** Shares of `income`; all null without a paycheck. Sum to 100.0. */
  percentages: Record<"bills" | "everyday" | "saving" | "debt" | "left_over", number | null>;
  window: { months: number };
  basis: Record<string, string>;
  /** Every income deposit per month, one-off money included. Not monthly income. */
  deposits_monthly_average: number;
  /** Income (b): paychecks left out of `income` (stopped, waiting). Optional for an
   *  older cached payload. */
  income_streams_excluded?: IncomeStreamFlag[];
  /** Income (b): counted only because the user kept them (undo offered). */
  income_streams_kept?: IncomeStreamFlag[];
}

/** Income (b), Paul 2026-10-15: a paycheck no recent deposit matched. */
export interface IncomeStreamFlag {
  id: number;
  name: string;
  reason: "stopped" | "waiting" | "kept";
  /** ISO date of the newest matching deposit, or null if none ever arrived. */
  last_deposit_date: string | null;
  kept_on: string | null;
}

export interface InsightSnapshot {
  months_analyzed: number;
  /** Monthly income (the typical month's) when a paycheck is found. */
  income_monthly: number;
  /** Every income deposit over the window: a deposits total, not income. */
  total_income: number;
  /** RD-11: every income deposit per month ("Average deposits, last 12 months"). */
  deposits_monthly?: number;
  /** RD-11. Optional only so an older cached payload still reads. */
  typical_month?: TypicalMonth;
  segments: InsightSnapshotSegment[];
  headline: string;
  /** Optional for backward compat with a pre-4a backend. */
  equation?: InsightEquation;
}

export interface InsightRecentTransaction {
  id: number;
  merchant: string;
  date: string;
  amount: number;
}

export interface InsightWantsCategory {
  category_id: number;
  name: string;
  icon: string;
  amount: number;
  pct: number;
  transaction_count: number;
  top_merchants: string[];
  recent_transactions: InsightRecentTransaction[];
  tip: string;
}

export interface InsightWants {
  window_days: number;
  stats: {
    total_spent: number;
    transaction_count: number;
    monthly_avg: number;
    pct_of_income: number;
  };
  breakdown: InsightWantsCategory[];
}

export interface InsightSubscriptionItem {
  id: number;
  merchant: string;
  monthly_price: number;
  amount: number;
  frequency: string;
  last_date: string | null;
  next_date: string | null;
  suggested_status: "keep" | "review";
  suggested_reason: string;
}

export interface InsightSubscriptions {
  stats: { total_spent: number; count: number; monthly_avg: number };
  items: InsightSubscriptionItem[];
}

export interface InsightAdjustOption {
  id: "keep" | "reduce-slightly" | "reduce-significantly";
  pct: number;
}

export interface InsightFound {
  suggested_allocation: { splash_fund: number; debt_payoff: number; total: number };
}

export interface InsightPlanEntry {
  key: string;
  label: string;
  amount: number;
  descriptor: string;
}

export interface InsightsContract {
  snapshot: InsightSnapshot;
  wants: InsightWants;
  subscriptions: InsightSubscriptions;
  adjust: { options: InsightAdjustOption[] };
  found: InsightFound;
  quick_tip: { message: string };
  plan_at_a_glance: {
    cadence: string;
    entries: InsightPlanEntry[];
    /** Names the income base this card rests on, so it cannot be confused with the
     *  Forecast's "Pay Period Forecast" — which shows the same three buckets for the
     *  same period off a DIFFERENT base (50/30/20 of PROJECTED income, vs this
     *  card's prorated ACTUAL budgets). Both are legitimate; the labels must say
     *  which is which (BEN-141 4a D5). Optional for backward compat. */
    basis?: string;
    basis_label?: string;
  };
}

/* ─────────────────────────────── fetcher ─────────────────────────────── */

export async function getInsights(): Promise<InsightsContract> {
  const { data } = await apiClient.get<InsightsContract>("/insights/");
  return data;
}

/* ───────────────────── adapters: insights → dashboard cards ─────────────────
 * The two remaining dashboard mock cards (Plan-at-a-Glance, Quick Tip) read
 * from this endpoint. The plan entry's `key` already matches PlanAtGlance's
 * STYLES keys (needs/wants/savings/splash-fund/debt-allocation), so it maps
 * straight onto the `color` field. */

/** 50/30/20 allocation tiles — a MONTHLY "habits" view. Income + needs/wants/
 *  savings (amount + %) come straight from the Insights snapshot's trailing-12mo
 *  basis, so the Dashboard tiles and the Insights page read the SAME source and
 *  can never disagree (cross-page alignment). A trailing numerator + denominator
 *  also makes the 50/30/20 target comparison fair on ANY viewing day — no
 *  early-month ramp (#Flag 4). Splash/Debt are the MONTHLY suggested allocation
 *  (`found`), matching the row's monthly window. */
export function toAllocationCards(data: InsightsContract | undefined): AllocationCard[] {
  if (!data) return [];
  const { snapshot, found } = data;
  // RD-11, option (a): with no paycheck found there is no income, so no share of it.
  // The legacy field still carries the deposit average for older clients; reading 0
  // here makes every share on these cards "no income figure yet" (D4).
  const income =
    snapshot.typical_month && !snapshot.typical_month.has_paycheck ? 0 : snapshot.income_monthly;
  const seg = (key: "needs" | "wants" | "savings") => snapshot.segments.find((s) => s.key === key);
  const splash = found.suggested_allocation.splash_fund;
  const debt = found.suggested_allocation.debt_payoff;
  const pct = (n: number) => (income > 0 ? Math.round((n / income) * 1000) / 10 : 0);

  return [
    {
      id: "income",
      label: bucketLabel("income"),
      amount: income,
      percentOfIncome: 100,
      icon: "dollar",
      color: "income",
    },
    {
      id: "needs",
      label: bucketLabel("needs"),
      amount: seg("needs")?.monthly ?? 0,
      percentOfIncome: seg("needs")?.pct ?? 0,
      icon: "home",
      color: "needs",
    },
    {
      id: "wants",
      label: bucketLabel("wants"),
      amount: seg("wants")?.monthly ?? 0,
      percentOfIncome: seg("wants")?.pct ?? 0,
      icon: "shopping",
      color: "wants",
    },
    {
      id: "savings",
      label: bucketLabel("savings"),
      amount: seg("savings")?.monthly ?? 0,
      percentOfIncome: seg("savings")?.pct ?? 0,
      icon: "piggy",
      color: "savings",
    },
    // BEN-141. These two are SUGGESTIONS — a slice of the trailing monthly net
    // surplus — and they printed "Splash Fund" / "Debt Payoff", the same strings
    // Plan at a Glance and the equation row print for two other quantities on the
    // same page. Nothing about the derivation changed; the labels now say which
    // figure this is and the descriptors name the window.
    {
      id: "splash-fund",
      label: "Suggested Splash Fund",
      amount: splash,
      percentOfIncome: pct(splash),
      icon: "sparkles",
      color: "splash-fund",
      isNew: true,
      descriptor: "Suggested from your average monthly surplus",
    },
    {
      id: "debt",
      label: "Suggested Debt Payoff",
      amount: debt,
      percentOfIncome: pct(debt),
      icon: "credit",
      color: "debt-allocation",
      descriptor: "Suggested from your average monthly surplus",
    },
  ];
}

/**
 * COMPLETE months behind the snapshot's monthly averages (BEN-141 1c/1d).
 *
 * The 50/30/20 tiles and the rule tile are trailing monthly averages, so they
 * inherit the same basis caveat as the FA sections — `months_analyzed` mirrors
 * `cash_flow.months_used` on the backend precisely so these surfaces cannot
 * claim different windows. 0 means the divisor fell back to a partial month.
 * Returns null when there is no payload at all, which the tiles treat as "not
 * loaded" rather than "insufficient".
 */
export function toBasisMonths(data: InsightsContract | undefined): number | null {
  return data ? data.snapshot.months_analyzed : null;
}

/** Plan at a Glance rows that are this period's budget for a #9 bucket. */
const PLAN_BUDGET_KEYS = new Set(["needs", "wants", "savings"]);

export function toPlanEntries(data: InsightsContract | undefined): PlanEntry[] {
  if (!data) return [];
  // FP-062: with no detected paycheck the server's window is the month.
  const window = data.plan_at_a_glance.cadence === "this-month" ? "this month" : "this period";
  return data.plan_at_a_glance.entries.map((e) => ({
    // Backend sends pre-rendered labels — re-label to Anthony's #9 names. The three
    // bucket figures are this period's prorated budget, and say so (#1392, names
    // from the vocabulary classification); the target line arrives named.
    label: PLAN_BUDGET_KEYS.has(e.key)
      ? `${bucketLabel(e.key)} budget ${window}`
      : relabelBucket(e.label),
    amount: e.amount,
    color: e.key,
    descriptor: e.descriptor,
  }));
}

export function toQuickTip(data: InsightsContract | undefined): QuickTip | null {
  if (!data) return null;
  return { message: data.quick_tip.message };
}
